import { useState, useEffect, useCallback, useMemo } from "react";
import api, { formatError } from "@/lib/api";
import { Trash2, X, Search, RotateCcw, ExternalLink } from "lucide-react";

const AREA_TABS = [
  { key: "SOCIAL", label: "Social (Feed)", kind: "post", scope: "SOCIAL" },
  { key: "STX_FEED", label: "stilltouchedX (Feed)", kind: "post", scope: "STILLTOUCHEDX" },
  { key: "MARKETPLACE", label: "Marketplace", kind: "listing" },
  { key: "STILLTOUCHEDX", label: "stilltouchedX (Artikel)", kind: "listing" },
  { key: "TRASH", label: "Papierkorb", kind: null },
];

const PAGE_SIZE = 30;

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function ContentModerationSection() {
  const [tab, setTab] = useState("SOCIAL");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [uploaderQuery, setUploaderQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [sort, setSort] = useState("date_desc");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Delete-confirmation modal
  const [showConfirm, setShowConfirm] = useState(false);
  const [reason, setReason] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleteResult, setDeleteResult] = useState(null);

  const currentTab = useMemo(() => AREA_TABS.find((t) => t.key === tab), [tab]);
  const isPostsMode = currentTab?.kind === "post";
  const isTrashMode = tab === "TRASH";
  const idKey = isTrashMode ? "id" : (isPostsMode ? "post_id" : "listing_id");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(uploaderQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [uploaderQuery]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams({
        sort: isTrashMode ? (sort === "date_asc" ? "deleted_asc" : "deleted_desc") : sort,
        limit: String(PAGE_SIZE),
        skip: String(page * PAGE_SIZE),
      });
      if (debounced) params.set("uploader_query", debounced);
      let url;
      if (isTrashMode) {
        url = `/admin/content/trash?${params.toString()}`;
      } else if (isPostsMode) {
        if (currentTab?.scope) params.set("area_scope", currentTab.scope);
        url = `/admin/content/posts?${params.toString()}`;
      } else {
        params.set("area_scope", tab);
        url = `/admin/content/listings?${params.toString()}`;
      }
      const { data } = await api.get(url);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(formatError(e));
    } finally { setLoading(false); }
  }, [tab, debounced, sort, page, isPostsMode, isTrashMode]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setSelectedIds(new Set());
    setPage(0);
    // Close confirm-modal on tab/filter/sort change (avoids stale delete against wrong tab)
    setShowConfirm(false);
    setDeleteResult(null);
    setDeleteError("");
  }, [tab, debounced, sort]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const nx = new Set(prev);
      if (nx.has(id)) nx.delete(id); else nx.add(id);
      return nx;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === items.length && items.length > 0) setSelectedIds(new Set());
    else setSelectedIds(new Set(items.map((it) => it[idKey])));
  };

  const openConfirm = () => {
    if (selectedIds.size === 0) return;
    setReason(""); setDeleteError(""); setDeleteResult(null); setShowConfirm(true);
  };

  const doDelete = async () => {
    setDeleting(true); setDeleteError("");
    try {
      const payload = {
        // hard-delete has no user-facing reason (item was already trashed with a reason earlier)
        ...(isTrashMode ? {} : { reason: reason.trim() }),
        items: Array.from(selectedIds).map((id) => {
          if (isTrashMode) {
            const it = items.find((x) => x.id === id);
            return { kind: it?.kind || "post", id };
          }
          return { kind: currentTab.kind, id };
        }),
      };
      const url = isTrashMode ? "/admin/content/trash/hard-delete" : "/admin/content/moderate";
      const { data } = await api.post(url, payload);
      // Normalize hard-delete response into deleted/skipped/notified shape used by the modal
      const normalized = isTrashMode
        ? { deleted: data.purged || 0, skipped: data.skipped || 0, notified: 0 }
        : data;
      setDeleteResult(normalized);
      setSelectedIds(new Set());
      await load();
    } catch (e) {
      setDeleteError(formatError(e));
    } finally { setDeleting(false); }
  };

  const doRestore = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`${selectedIds.size} Objekt(e) wiederherstellen? Sie sind danach wieder öffentlich sichtbar.`)) return;
    try {
      const payload = {
        items: Array.from(selectedIds).map((id) => {
          const it = items.find((x) => x.id === id);
          return { kind: it?.kind || "post", id };
        }),
      };
      const { data } = await api.post("/admin/content/restore", payload);
      alert(`${data.restored} wiederhergestellt, ${data.skipped} übersprungen.`);
      setSelectedIds(new Set());
      await load();
    } catch (e) {
      alert(formatError(e));
    }
  };

  const closeConfirm = () => {
    setShowConfirm(false); setReason(""); setDeleteError(""); setDeleteResult(null);
  };

  // iter · „Im Frontend öffnen" pro Zeile (analog alter Beitrags-Verwaltung)
  const openInFrontend = (it, rowKind) => {
    if (rowKind === "post") {
      const pid = it.post_id || it.id;
      if (it.author_handle) {
        window.open(`/user/${it.author_handle}?post=${pid}#post-${pid}`, "_blank", "noopener,noreferrer");
      } else {
        window.open(`/post/${pid}`, "_blank", "noopener,noreferrer");
      }
    } else {
      const lid = it.listing_id || it.id;
      const scope = it.area_scope || (tab === "STILLTOUCHEDX" ? "STILLTOUCHEDX" : "MARKETPLACE");
      const path = scope === "STILLTOUCHEDX" ? `/stilltouchedx/${lid}` : `/marketplace/${lid}`;
      window.open(path, "_blank", "noopener,noreferrer");
    }
  };

  const pageTotal = Math.ceil(total / PAGE_SIZE);
  const allSelected = items.length > 0 && selectedIds.size === items.length;

  return (
    <div data-testid="content-mod-section" className="space-y-4">
      <div>
        <h1 className="text-xl font-bold">Content-Moderation</h1>
        <p className="text-xs text-[#666] mt-1">Beiträge (Social) und Artikel (Marketplace / stilltouchedX) verwalten. Filter nach User, sortieren, mehrere auswählen und mit Begründung entfernen.</p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-[#E5E5E5]">
        {AREA_TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`content-mod-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-[#666] hover:text-[#0A0A0A]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
          <input
            data-testid="content-mod-search"
            value={uploaderQuery}
            onChange={(e) => setUploaderQuery(e.target.value)}
            placeholder={isPostsMode ? "Nach Autor, Handle oder Beitragstext filtern" : "Nach Verkäufer, Handle oder Artikelname/-nummer filtern"}
            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-[#E5E5E5] rounded focus:outline-none focus:border-[#0A0A0A]"
          />
        </div>
        <select
          data-testid="content-mod-sort"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="text-sm bg-white border border-[#E5E5E5] rounded px-3 py-2"
        >
          <option value="date_desc">Neueste zuerst</option>
          <option value="date_asc">Älteste zuerst</option>
        </select>
        <div className="ml-auto text-xs text-[#666]">
          {selectedIds.size > 0 && (
            <span className="mr-3 font-medium text-[#0A0A0A]">{selectedIds.size} ausgewählt</span>
          )}
          <span>{total} gesamt</span>
        </div>
        <button
          data-testid="content-mod-delete-btn"
          onClick={openConfirm}
          disabled={selectedIds.size === 0}
          className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded ${
            selectedIds.size === 0
              ? "bg-[#F0F0F0] text-[#999] cursor-not-allowed"
              : "bg-red-600 hover:bg-red-700 text-white"
          }`}
        >
          <Trash2 size={14} /> {isTrashMode ? "Endgültig entfernen" : "Löschen"}
        </button>
        {isTrashMode && (
          <button
            data-testid="content-mod-restore-btn"
            onClick={doRestore}
            disabled={selectedIds.size === 0}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded ${
              selectedIds.size === 0
                ? "bg-[#F0F0F0] text-[#999] cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700 text-white"
            }`}
          >
            <RotateCcw size={14} /> Wiederherstellen
          </button>
        )}
      </div>

      {/* Info banner for trash mode */}
      {isTrashMode && (
        <div className="px-3 py-2 bg-amber-50 border border-amber-200 text-xs text-amber-900 rounded">
          Papierkorb — gelöschte Beiträge und Artikel bleiben hier <strong>30 Tage</strong> und können wiederhergestellt werden. Danach werden sie automatisch endgültig entfernt.
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-[#E5E5E5] rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
            <tr>
              <th className="w-10 px-3 py-2 text-left">
                <input
                  data-testid="content-mod-select-all"
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                />
              </th>
              <th className="px-3 py-2 text-left w-16">Bild</th>
              <th className="px-3 py-2 text-left">{isPostsMode ? "Text" : (isTrashMode ? "Titel / Text" : "Titel")}</th>
              <th className="px-3 py-2 text-left w-40">Autor / Verkäufer</th>
              {isTrashMode ? (
                <>
                  <th className="px-3 py-2 text-left w-24">Typ</th>
                  <th className="px-3 py-2 text-left w-40">Gelöscht am</th>
                  <th className="px-3 py-2 text-left w-32">Endgültig weg in</th>
                </>
              ) : (
                <>
                  {!isPostsMode && <th className="px-3 py-2 text-left w-24">Preis</th>}
                  <th className="px-3 py-2 text-left w-40">Erstellt</th>
                </>
              )}
              <th className="px-3 py-2 text-left w-28">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={isTrashMode ? 8 : (isPostsMode ? 6 : 7)} className="px-3 py-6 text-center text-[#999]">Lade…</td></tr>
            )}
            {!loading && error && (
              <tr><td colSpan={isTrashMode ? 8 : (isPostsMode ? 6 : 7)} className="px-3 py-6 text-center text-red-600">{error}</td></tr>
            )}
            {!loading && !error && items.length === 0 && (
              <tr><td colSpan={isTrashMode ? 8 : (isPostsMode ? 6 : 7)} className="px-3 py-6 text-center text-[#999]">
                {isTrashMode ? "Papierkorb ist leer." : "Keine Einträge gefunden."}
              </td></tr>
            )}
            {!loading && !error && items.map((it, idx) => {
              const id = it[idKey];
              const rowKind = isTrashMode ? it.kind : (isPostsMode ? "post" : "listing");
              const img = rowKind === "post" ? it.media_url : (it.media_urls && it.media_urls[0]);
              const label = isTrashMode ? it.title_or_text : (isPostsMode ? (it.text || "—") : (it.title || "—"));
              // Purge countdown for trash
              let daysLeft = null;
              if (isTrashMode && it.purge_at) {
                try {
                  const diffMs = new Date(it.purge_at) - new Date();
                  daysLeft = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
                } catch { /* ignore */ }
              }
              return (
                <tr key={`${rowKind || "row"}-${id || `idx${idx}`}`} className={`border-b border-[#F0F0F0] hover:bg-[#FAFAFA] ${selectedIds.has(id) ? "bg-[#FFFDF7]" : ""}`}>
                  <td className="px-3 py-2 align-middle">
                    <input
                      data-testid={`content-mod-row-check-${id}`}
                      type="checkbox"
                      checked={selectedIds.has(id)}
                      onChange={() => toggleSelect(id)}
                    />
                  </td>
                  <td className="px-3 py-2">
                    {img ? (
                      <img src={img} alt="" className="w-12 h-12 object-cover rounded border border-[#E5E5E5]" onError={(e) => { e.currentTarget.style.opacity = 0.2; }} />
                    ) : (
                      <div className="w-12 h-12 bg-[#F5F5F5] rounded border border-[#E5E5E5]" />
                    )}
                  </td>
                  <td className="px-3 py-2 max-w-[420px]">
                    <div className="line-clamp-2 text-[#0A0A0A]">{label}</div>
                    {rowKind === "listing" && it.public_item_no && (
                      <div className="text-[10px] text-[#888] mt-0.5">{it.public_item_no}</div>
                    )}
                    {isTrashMode && it.moderation_reason && (
                      <div className="text-[10px] text-red-700 mt-0.5">Begründung: {it.moderation_reason}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[#333]">
                    <div className="font-medium">{it.author_display_name}</div>
                    {it.author_handle && <div className="text-[10px] text-[#888]">@{it.author_handle}</div>}
                  </td>
                  {isTrashMode ? (
                    <>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] px-2 py-0.5 border ${rowKind === "post" ? "bg-blue-50 border-blue-200 text-blue-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}>
                          {rowKind === "post" ? "Beitrag" : "Artikel"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-[#666] text-xs">{fmtDate(it.deleted_at)}</td>
                      <td className="px-3 py-2 text-xs">
                        {daysLeft != null ? (
                          <span className={daysLeft <= 3 ? "text-red-600 font-medium" : "text-[#666]"}>
                            {daysLeft} Tag{daysLeft === 1 ? "" : "e"}
                          </span>
                        ) : "—"}
                      </td>
                    </>
                  ) : (
                    <>
                      {!isPostsMode && (
                        <td className="px-3 py-2 text-[#333]">
                          {it.price != null ? `€ ${Number(it.price).toFixed(2)}` : "—"}
                        </td>
                      )}
                      <td className="px-3 py-2 text-[#666] text-xs">{fmtDate(it.created_at || it.published_at)}</td>
                    </>
                  )}
                  <td className="px-3 py-2">
                    {isTrashMode ? (
                      <button
                        data-testid={`content-mod-single-restore-${id}`}
                        onClick={() => { setSelectedIds(new Set([id])); doRestore(); }}
                        className="text-xs text-green-700 hover:underline"
                      >Wiederherstellen</button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`content-mod-single-open-${id}`}
                          onClick={() => openInFrontend(it, rowKind)}
                          title="Im Frontend öffnen (neuer Tab)"
                          className="inline-flex items-center gap-1 text-xs text-[#0A0A0A] hover:underline"
                        ><ExternalLink size={13} /> Öffnen</button>
                        <button
                          data-testid={`content-mod-single-delete-${id}`}
                          onClick={() => { setSelectedIds(new Set([id])); openConfirm(); }}
                          className="text-xs text-red-600 hover:underline"
                        >Löschen</button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pageTotal > 1 && (
        <div className="flex items-center justify-between text-xs text-[#666]">
          <span>Seite {page + 1} / {pageTotal}</span>
          <div className="flex gap-2">
            <button
              data-testid="content-mod-prev-page"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1 rounded border border-[#E5E5E5] disabled:opacity-40 hover:bg-[#F5F5F5]"
            >Zurück</button>
            <button
              data-testid="content-mod-next-page"
              onClick={() => setPage((p) => Math.min(pageTotal - 1, p + 1))}
              disabled={page >= pageTotal - 1}
              className="px-3 py-1 rounded border border-[#E5E5E5] disabled:opacity-40 hover:bg-[#F5F5F5]"
            >Weiter</button>
          </div>
        </div>
      )}

      {/* Confirm modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div data-testid="content-mod-confirm-modal" className="bg-white rounded-lg max-w-lg w-full shadow-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E5E5]">
              <h3 className="font-semibold text-[#0A0A0A]">
                {isTrashMode
                  ? `${selectedIds.size} Objekt${selectedIds.size > 1 ? "e" : ""} endgültig entfernen`
                  : `${selectedIds.size} ${isPostsMode ? "Beitrag" : "Artikel"}${selectedIds.size > 1 ? (isPostsMode ? "e" : "") : ""} entfernen`}
              </h3>
              <button onClick={closeConfirm} className="text-[#666] hover:text-[#0A0A0A]"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              {!deleteResult ? (
                <>
                  {isTrashMode ? (
                    <p className="text-sm text-[#333]">
                      <strong className="text-red-700">Achtung:</strong> Diese Aktion entfernt {selectedIds.size} Objekt{selectedIds.size > 1 ? "e" : ""} <strong>unwiderruflich</strong> aus der Datenbank. Eine Wiederherstellung ist danach nicht mehr möglich.
                    </p>
                  ) : (
                    <>
                      <p className="text-sm text-[#333]">
                        Diese Aktion setzt den Status auf „entfernt&#8221;. {selectedIds.size} Objekt{selectedIds.size > 1 ? "e" : ""} werden aus den öffentlichen Feeds ausgeblendet.
                      </p>
                      <div>
                        <label className="text-xs font-medium text-[#666] block mb-1">
                          Begründung (optional – wenn ausgefüllt, wird der Ersteller informiert):
                        </label>
                        <textarea
                          data-testid="content-mod-reason"
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder="z. B. Spam, Richtlinienverstoß, unerlaubter Inhalt, …"
                          rows={4}
                          maxLength={500}
                          className="w-full px-3 py-2 text-sm bg-white border border-[#E5E5E5] rounded focus:outline-none focus:border-[#0A0A0A] resize-none"
                        />
                        <div className="text-[10px] text-[#999] mt-1">{reason.length}/500</div>
                      </div>
                    </>
                  )}
                  {deleteError && <div className="text-xs text-red-600">{deleteError}</div>}
                  <div className="flex items-center justify-end gap-2 pt-2">
                    <button onClick={closeConfirm} className="px-4 py-2 text-sm text-[#666] hover:text-[#0A0A0A]">Abbrechen</button>
                    <button
                      data-testid="content-mod-confirm-delete"
                      onClick={doDelete}
                      disabled={deleting}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50"
                    >
                      <Trash2 size={14} /> {deleting ? "Lösche…" : (isTrashMode ? "Endgültig entfernen" : "Endgültig entfernen")}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm space-y-1">
                    <div><strong>{deleteResult.deleted}</strong> Objekt{deleteResult.deleted === 1 ? "" : "e"} {isTrashMode ? "endgültig entfernt" : "entfernt"}.</div>
                    {deleteResult.notified > 0 && (
                      <div><strong>{deleteResult.notified}</strong> Ersteller wurden mit Begründung benachrichtigt.</div>
                    )}
                    {deleteResult.skipped > 0 && (
                      <div className="text-[#999]">{deleteResult.skipped} übersprungen (bereits entfernt oder nicht gefunden).</div>
                    )}
                  </div>
                  <div className="flex items-center justify-end pt-2">
                    <button data-testid="content-mod-close-result" onClick={closeConfirm} className="px-4 py-2 text-sm bg-[#0A0A0A] hover:bg-[#333] text-white rounded">
                      Fertig
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
